import { APIClient } from '@/services'
import { View, Image, Text, ActionIcon } from '@/components'
import { memoize } from '@codeleap/utils'
import { variantProvider } from '@/app'
import { achievementsStore } from '@/stores'
import { AnimatePresence, motion } from 'framer-motion'

const UnMemoizedAchievementToast = () => {
  const isVisible = achievementsStore.state.use(s => s.toastVisible)
  const levels = achievementsStore.state.use(s => s.levels)

  const level = levels?.[0] ?? null

  const handleView = async () => {
    achievementsStore.closeToast()
    achievementsStore.toggleModal(level?.achievement)
  }

  return <AnimatePresence>
    {isVisible ? (
      <View style={styles.wrapper}>
        <motion.div
          initial={{
            opacity: 0,
            y: -20,
            scale: 0.9
          }}
          animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }}
          exit={{
            opacity: 0,
            y: -20,
            scale: 0.9
          }}
          transition={{
            duration: 0.2,
            ease: "easeOut"
          }}
          style={styles.innerWrapper}
        >
          <View css={[styles.wrapperImage, { background: level?.bg_color_light }]}>
            <Image style={styles.image} source={level?.image} objectFit='contain' />
          </View>

          <View variants={['column', 'alignStart', 'justifyStart']}>
            <Text text={level?.congratulatory_message} variants={['p2', 'bold', 'color:neutral6']} style={{ lineHeight: 1 }} />
            <Text text={level?.description} variants={['p4', 'regular', 'color:neutral5']} />
          </View>

          <View variants={['row', 'center', 'gap:1']}>
            <View
              onClick={handleView}
              css={[styles.buttonView, { background: level?.bg_color_light }]}
            >
              <Text text={'View'} variants={['p2', 'bold']} style={{ color: level?.bg_color }} />
            </View>

            <View style={styles.divider} />

            <ActionIcon
              debugName='toast:close'
              name='x'
              variants={['normalize', 'neutral9', 'iconSize:1']}
              onPress={() => achievementsStore.closeToast()}
            />
          </View>
        </motion.div>
      </View>
    ) : null}
  </AnimatePresence>
}

export const AchievementToast = memoize(UnMemoizedAchievementToast)

const imageSize = {
  wrapper: 36,
  img: 20,
}

const styles = variantProvider.createComponentStyle(theme => ({
  wrapper: {
    position: 'fixed',
    top: theme.values.headerHeight + theme.spacing.value(3),
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: Number.MAX_SAFE_INTEGER,
  },
  innerWrapper: {
    boxShadow: '0px 8px 24px 0px #99999933',
    background: theme.colors.neutral1,
    // @ts-ignore
    borderRadius: theme.borderRadius.xTiny,
    padding: theme.spacing.value(1),
    paddingRight: theme.spacing.value(1.5),
    gap: theme.spacing.value(1),
    ...theme.presets.row,
    ...theme.presets.center,
  },
  wrapperImage: {
    // @ts-ignore
    borderRadius: theme.borderRadius.xTiny / 2,
    minWidth: imageSize.wrapper,
    maxWidth: imageSize.wrapper,
    minHeight: imageSize.wrapper,
    maxHeight: imageSize.wrapper,
    ...theme.presets.center,
  },
  image: {
    height: imageSize.img,
    width: imageSize.img,
  },
  buttonView: {
    width: 64,
    height: 36,
    padding: theme.spacing.value(1),
    borderRadius: theme.borderRadius.tiny,
    ...theme.presets.center,
    cursor: 'pointer',
    transition: 'all 0.2s',
    ':hover': {
      filter: 'brightness(90%)'
    }
  },
  divider: {
    backgroundColor: theme.colors.neutral3,
    height: 36,
    width: 1,
  }
}), true)
